// Â© 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import type { LoadedMachine, MachineDefinition } from '@core/machine'
import type { Ramp } from '@core/sim/devices/Ramp'
import type { WireGuide } from '@core/sim/devices/WireGuide'
import { lightingProfileFor } from './lightingProfiles'
import {isScoopKicker} from './componentKickerPresentation'
import {manufacturedScoopRadius} from './componentKickerGeometry'
import {rolloverPlacement} from './componentRolloverPlacement'

export const MAX_LIGHTING_EMITTERS = 192
export interface ComponentLightSite {
  readonly owner: string; readonly part: string; readonly lamp: string; readonly role: 'lens' | 'path' | 'post' | 'rail' | 'gi'
  readonly x: number; readonly y: number; readonly z: number; readonly radius: number; readonly phase: number
}
/** All positions are playfield XY / height Z; paths come from the same samples the ball rides. */
export function componentLightLayout(definition: MachineDefinition, machine?: LoadedMachine): ComponentLightSite[] {
  const guides = lightingProfileFor(definition.machineId).paths
  const sites: ComponentLightSite[] = []
  const add = (site: ComponentLightSite): void => { if (sites.length < MAX_LIGHTING_EMITTERS) sites.push(site) }
  for (const d of definition.devices) {
    let point: readonly [number, number] | undefined, radius = 0.008, z = 0.023
    if (d.kind === 'popBumper') { point = d.centre; radius = d.skirtRadius * 0.55; z = 0.034 }
    else if(d.kind==='autoPlunger'){
      const h=Math.hypot(...d.exit.direction),dx=d.exit.direction[0]/h,dy=d.exit.direction[1]/h
      for(const x of [-.0105,.0105])add({owner:d.id,part:d.id,lamp:d.lamp??'',role:'lens',x:d.centre[0]+x*dy+.007*dx,y:d.centre[1]-x*dx+.007*dy,z:.00025,radius:.0011,phase:0})
      continue
    }
    else if (d.kind === 'kicker') {
      const scoop=isScoopKicker(d),r=scoop?manufacturedScoopRadius(d.captureRadius):Math.max(.017,d.captureRadius),length=Math.hypot(...d.exit.direction)
      const dx=d.exit.direction[0]/length,dy=d.exit.direction[1]/length
      for(let i=0;i<(scoop?4:2);i++){
        const x=scoop?(r-.00045)*Math.cos(i*Math.PI/2):(i===0?-1:1)*(r+.0015),y=scoop?(r-.00045)*Math.sin(i*Math.PI/2):-.012
        add({owner:d.id,part:d.id,lamp:d.lamp??'',role:'lens',x:d.centre[0]+x*dy+y*dx,y:d.centre[1]-x*dx+y*dy,z:scoop?.00075:.00285,radius:scoop?.0025:.0011,phase:0})
      }
      continue
    }
    else if (d.kind === 'slingshot' || d.kind === 'standupTarget' || d.kind === 'dropTarget' || d.kind === 'spinner' || d.kind === 'gate' || d.kind === 'diverter' || d.kind === 'ballSaveGate') {
      point = [(d.a[0] + d.b[0]) / 2, (d.a[1] + d.b[1]) / 2]
      if (d.kind === 'slingshot') { radius = 0.016; z = 0.033 }
      if (d.kind === 'spinner') z = 0.041
    } else if (d.kind === 'rollover') {
      const p=rolloverPlacement(d,definition),c=Math.cos(p.bearing),s=Math.sin(p.bearing)
      for(const side of [-1,1]){const x=side*(p.span+.0025),y=.0035;add({owner:d.id,part:d.id,lamp:d.lamp??'',role:'lens',x:p.x+c*x-s*y,y:p.y+s*x+c*y,z:.00005,radius:.00085,phase:0})}
      continue
    }
    if (point === undefined) continue
    add({ owner: d.id, part: d.id, lamp: d.lamp ?? '', role: 'lens', x: point[0], y: point[1], z, radius, phase: 0 })
  }
  for (const l of definition.layers) {
    if (l.kind !== 'ramp' && l.kind !== 'wire') continue
    const runtime = machine?.devices.get(l.id) as Ramp | WireGuide | undefined
    if (runtime === undefined) continue
    const path = runtime.path
    let lamp = guides !== undefined && Object.hasOwn(guides, l.id) ? guides[l.id]! : '', best = lamp === '' ? 0.14 ** 2 : 0
    for (const bulb of definition.lamps ?? []) {
      if (bulb.kind !== 'insert' || bulb.position === undefined) continue
      const d = (bulb.position[0] - path.xs[0]!) ** 2 + (bulb.position[1] - path.ys[0]!) ** 2
      if (d < best) { best = d; lamp = bulb.id }
    }
    for (let i = 0; i < 10; i++) {
      const n = Math.round(i / 9 * (path.sampleCount - 1))
      const j = Math.min(n, path.sampleCount - 2), dx = path.xs[j + 1]! - path.xs[j]!, dy = path.ys[j + 1]! - path.ys[j]!
      const length = Math.sqrt(dx * dx + dy * dy), offset = l.kind === 'ramp' ? l.width / 2 : 0.014
      add({ owner: l.id, part: l.id, lamp, role: 'path', x: path.xs[n]! - (length > 1e-12 ? dy / length * offset : 0),
        y: path.ys[n]! + (length > 1e-12 ? dx / length * offset : 0), z: path.zs[n]! + 0.010, radius: 0.0045, phase: i / 9 })
    }
  }
  const { width, length } = definition.dimensions.playfield
  for (let i = 0; i < 12; i++) for (const side of [-1, 1]) {
    add({ owner: '@ambient', part: '@rail', lamp: '', role: 'rail', x: side * (width / 2 + 0.006),
      y: -length / 2 + (i + 0.5) / 12 * length, z: 0.027, radius: 0.004, phase: i / 11 })
  }
  for (const side of [-1, 1]) add({ owner: '@ambient', part: '@gi', lamp: '', role: 'gi', x: side * width * 0.26, y: length * 0.1,
    z: 0.0015, radius: Math.min(width, length) * 0.24, phase: 0 })
  return sites
}
